import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import {
  getUserProfileById,
  getEmployeeDesignationByUserId,
  getVerticals,
  getLocationsByVertical,
  getDepartments,
} from "../api/hrService";
import api from "../../../api/api";
import { useNavigate, useParams } from "react-router-dom";

/* =========================
   TYPES
========================= */
interface ViewUser {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  gender: string;
  dob: string;
  mobile: string;
  personalEmail: string;
  officeEmail: string;
  status: string;
  currentAddress: string;
  permanentAddress: string;

  verticalNames: string[];
  locationNames: string[];

  department: string;
  roles: string[];
  designation: string;
  reportingManager: string;
  salesTarget: string;
  joiningDate: string;
  leavingDate: string | null;


  username: string;
}

/* =========================
   UI HELPER
========================= */
const InfoRow = ({ label, value }: { label: string; value?: any }) => (
  <div className="d-flex justify-content-between py-1">
    <span className="text-muted">{label}</span>
    <span className="fw-semibold">
      {value !== undefined && value !== null && value !== "" ? value : "-"}
    </span>
  </div>
);

/* =========================
   COMPONENT
========================= */
const ViewHRUser: React.FC = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const uniqueNumbers = (arr: number[]) =>
    arr.filter((v, i, self) => self.indexOf(v) === i);

  const [data, setData] = useState<ViewUser>({
    id: "",
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    dob: "",
    mobile: "",
    personalEmail: "",
    officeEmail: "",
    status: "",
    currentAddress: "",
    permanentAddress: "",

    verticalNames: [],
    locationNames: [],

    department: "-",
    roles: [],
    designation: "-",
    reportingManager: "-",
    salesTarget: "-",
    joiningDate: "",
    leavingDate: null,
    username: "-",
  });

  /* =========================
     LOAD ALL DATA
  ========================= */
  useEffect(() => {
    if (!userId) return;
    loadProfileAndMappings(Number(userId));
  }, [userId]);

  const loadProfileAndMappings = async (userId: number) => {
    try {
      /* 1️⃣ PROFILE */
      const profileRes = await getUserProfileById(userId);
      const p = profileRes?.data?.data;
      if (!p) return;

      setData(prev => ({
        ...prev,
        firstName: p.first_name || "",
        middleName: p.middle_name || "",
        lastName: p.last_name || "",
        gender: p.gender_type || "",
        dob: p.dob || "",
        mobile: p.mobile || "",
        personalEmail: p.personal_email_id || "",
        officeEmail: p.official_mail_id || "",
        status: p.is_active || "",
        currentAddress: p.current_address || "",
        permanentAddress: p.permanent_address || "",
        joiningDate: p.joining_date || "",
        leavingDate: p.leaving_date || null,
      }));
      setData(prev => ({
        ...prev,
         roles: p.role || [], 
      }));
      /* 2️⃣ VERTICAL + LOCATION */
      await loadVerticalLocation(userId);

      /* 3️⃣ DESIGNATION → ROLE → RM → DEPARTMENT */
      await loadDesignationAndDepartment(userId);

    } catch (err) {
      console.error("ViewHRUser load failed", err);
    }
  };

  /* =========================
     VERTICAL + LOCATION
  ========================= */
  const loadVerticalLocation = async (userId: number) => {
    const res = await api.get(
      `/hr/v1/employee-vertical-location/?user_id=${userId}`
    );

    const results = res.data?.results || [];
    if (!results.length) return;

    const verticalIds = uniqueNumbers(results.map((r: any) => r.vertical));
    const locationIds = results.map((r: any) => r.location);

    const vertRes = await getVerticals();
    const vertMap = new Map(
      vertRes.data.results.map((v: any) => [v.id, v.name])
    );

    let locationNames: string[] = [];
    for (const vid of verticalIds) {
      const locRes = await getLocationsByVertical(vid);
      locRes.data.results.forEach((l: any) => {
        if (locationIds.includes(l.id)) {
          locationNames.push(l.name);
        }
      });
    }

    setData(prev => ({
      ...prev,
      verticalNames: verticalIds
        .map(id => vertMap.get(id))
        .filter((v): v is string => Boolean(v)),
      locationNames,
    }));
  };

  /* =========================
     DESIGNATION + DEPARTMENT
  ========================= */
  const loadDesignationAndDepartment = async (userId: number) => {
    const res = await getEmployeeDesignationByUserId(userId);
    const results = res.data?.results || [];
    if (!results.length) return;

    const latest = results[results.length - 1];

    setData(prev => ({
      ...prev,
      designation: latest.designation_id?.name || "-",
      reportingManager:
        latest.assign_reporting?.full_name ||
        `${latest.assign_reporting?.first_name || ""} ${latest.assign_reporting?.last_name || ""}`.trim() ||
        "-",
      salesTarget: latest.sales_target || "-",
    }));

    try {
      const deptRes = await api.get(
        `/hr/v1/employee-department/?user_id=${userId}`
      );

      const dept = deptRes.data?.results?.[0];
      if (!dept?.department_id) return;

      const deptMasterRes = await getDepartments();
      const deptList = deptMasterRes.data?.results || [];

      const deptName =
        deptList.find((d: any) => d.id === dept.department_id)?.name || "-";

      setData(prev => ({
        ...prev,
        department: deptName,
      }));
    } catch {
      // ignore
    }
  };
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth", // optional
    });
  }, []);

  /* =========================
     RENDER
  ========================= */
  return (
    <>
      <PageHeader
        title="View Employee"
        breadcrumbs={[
          { label: "Dashboard", path: "/dashboard" },
          { label: "HR" },
          { label: "Employee Master", path: "/hr/users" },
          { label: "View Employee" },
        ]}
      />

      <div className="container mt-4">
        <div className="card common-form-card p-4">

          {/* BASIC */}
          <Section title="Basic Information">
            <Info label="Full Name" value={`${data.firstName} ${data.middleName} ${data.lastName}`} />
            <Info label="Gender" value={data.gender} />
            <Info label="DOB" value={data.dob} />
            <Info label="Mobile" value={data.mobile} />
            <Info label="Personal Email" value={data.personalEmail} />
            <Info label="Official Email" value={data.officeEmail} />
            <Info label="Status" value={data.status} />
            <Info label="Joining Date" value={data.joiningDate} />

            {data.status === "Inactive" && (
              <Info label="Leaving Date" value={data.leavingDate || "-"} />
            )}
          </Section>

          {/* ADDRESS */}
          <Section title="Address">
            <Info label="Current Address" value={data.currentAddress} />
            <Info label="Permanent Address" value={data.permanentAddress} />
          </Section>

          {/* ORGANIZATION */}
          <Section title="Organization Mapping">
            <Info label="Vertical" value={data.verticalNames.join(", ")} />
            <Info label="Location" value={data.locationNames.join(", ")} />
          </Section>

          {/* PROFESSIONAL */}
          <Section title="Professional Details">
            <Info label="Department" value={data.department} />
            <Info label="Roles" value={data.roles.join(", ")} />
            <Info label="Designation" value={data.designation} />
            <Info label="Reporting Manager" value={data.reportingManager} />
            <Info label="Sales Target" value={data.salesTarget} />
          </Section>

          <div className="d-flex justify-content-end mt-4">
            <button className="btn btn-secondary" onClick={() => navigate("/hr/users")}>
              Back
            </button>
          </div>

        </div>
      </div>

    </>
  );
};

export default ViewHRUser;



const Section = ({ title, children }: any) => (
  <div className="form-section mt-4">
    <div className="form-section-header bg-primary text-white">
      {title}
    </div>
    <div className="row g-3 p-3">
      {children}
    </div>
  </div>
);

const Info = ({ label, value }: { label: string; value: string }) => (
  <div className="col-md-3">
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold">{value || "-"}</div>
  </div>
);